import React, { useState, useEffect, useRef } from "react";
import StoryMilestone from "./StoryMilestone";
import UserAvatar from "./UserAvatar";
import AvatarCustomizer from "./AvatarCustomizer";

const ProgressBar = React.memo(
  ({ progress, unitNumber, user, updateUserData, onProgressBarClick }) => {
    const percentage = Math.min(Math.max(progress, 0), 100);
    const [showMessage, setShowMessage] = useState(false);
    const [currentMessage, setCurrentMessage] = useState("");
    const [messageTitle, setMessageTitle] = useState("");
    const [showAvatarCustomizer, setShowAvatarCustomizer] = useState(false);
    const [hovering, setHovering] = useState(false);
    const progressBarRef = useRef(null);

    // Track highest milestone reached for each unit
    const highestMilestoneReachedRef = useRef({});

    // Add a ref to track previous unit number to prevent unnecessary resets
    const prevUnitNumberRef = useRef(unitNumber);

    // Add a ref to track if a milestone message is pending
    const milestonePendingRef = useRef(false);

    // Define milestone positions (percentage points)
    const milestones = [20, 40, 60, 80];

    // Get the user ID for localStorage tracking
    const userId = user?.id || "guest";

    // Load shown milestones from localStorage on component mount
    useEffect(() => {
      if (user) {
        // Only load from localStorage when user is available
        try {
          const storedMilestones = localStorage.getItem(
            `shown_milestones_${userId}`
          );
          if (storedMilestones) {
            const parsedMilestones = JSON.parse(storedMilestones);
            // Pre-populate the ref with loaded data
            shownMilestonesRef.current = parsedMilestones;
            console.log(
              "Loaded previously shown milestones:",
              parsedMilestones
            );
          }
        } catch (error) {
          console.error(
            "Error loading shown milestones from localStorage:",
            error
          );
        }
      }
    }, [userId, user]);

    // Initialize shown milestones ref with data from localStorage or empty object
    const shownMilestonesRef = useRef({});

    // Helper function to save shown milestones to localStorage
    const saveShownMilestones = (milestones) => {
      try {
        localStorage.setItem(
          `shown_milestones_${userId}`,
          JSON.stringify(milestones)
        );
      } catch (error) {
        console.error("Error saving shown milestones to localStorage:", error);
      }
    };

    // Define milestone icons and messages for each unit
    const unitStories = {
      1: {
        theme: "Growing Up",
        icons: {
          20: "baby",
          40: "child",
          60: "teen",
          80: "academy",
        },
        chapters: {
          20: "First Steps",
          40: "Childhood Years",
          60: "Academy Preparation",
          80: "Knight Academy Entrance",
        },
        titles: {
          20: "First Steps",
          40: "Growing Strong",
          60: "Academy Dreams",
          80: "Academy Gates",
        },
        messages: {
          20: "With wobbling knees and determination in your eyes, you take your first steps into the world! The village elder nods approvingly as you begin your journey toward becoming a knight.",
          40: "Years pass as you grow taller and stronger. You spend your days playing with wooden swords and shields, dreaming of adventures beyond the village. The local blacksmith notices your potential.",
          60: "The Knight Academy entrance exam is approaching! You spend every waking moment studying ancient texts and practicing your sword technique. Your parents beam with pride at your dedication.",
          80: "The golden gates of the Knight Academy stand before you! Your heart pounds as the gatekeeper examines your application. 'Welcome, young one,' he says with a smile. 'Your journey truly begins now.'",
        },
      },
      2: {
        theme: "Knight Training",
        icons: {
          20: "shield",
          40: "sword",
          60: "horse",
          80: "graduate",
        },
        chapters: {
          20: "Shield Training",
          40: "Sword Mastery",
          60: "Horseback Riding",
          80: "Knight Graduation",
        },
        titles: {
          20: "Shield Bearer",
          40: "Blade Master",
          60: "Noble Steed",
          80: "Knight's Honor",
        },
        messages: {
          20: "The weight of the shield feels strange on your arm. 'A knight's first duty is to protect!' your instructor bellows. After weeks of training, your muscles strengthen, and the shield becomes an extension of yourself.",
          40: "The master swordsman circles you slowly. 'Feel the blade as part of your arm,' he whispers. Through blisters and bruises, your technique improves until the sword dances in your hand like a silver flash of lightning.",
          60: "The royal stables introduce you to your training horse - a chestnut mare with kind eyes. At first, you fall often, but soon you're galloping through meadows, moving as one with your loyal companion.",
          80: "Standing tall in polished armor, you kneel before the King. The royal sword touches each shoulder as he pronounces you a Knight of the Realm. Your fellow graduates cheer as pride swells in your chest!",
        },
      },
      3: {
        theme: "The Quest",
        icons: {
          20: "map",
          40: "forest",
          60: "mountain",
          80: "oldman",
        },
        chapters: {
          20: "The Journey Begins",
          40: "Through the Forest",
          60: "Mountain Crossing",
          80: "Meeting the Gold Master",
        },
        titles: {
          20: "The Call",
          40: "Forest Shadows",
          60: "Mountain Peaks",
          80: "Ancient Wisdom",
        },
        messages: {
          20: "A sealed scroll arrives bearing the royal crest. Your first quest! The ancient map shows the path to the Gold Master, who will reveal the Dragon's location. With provisions packed and sword at your side, you set forth.",
          40: "The Whispering Forest engulfs you in mystery. Rays of light pierce through the canopy as strange creatures watch from the shadows. A woodland sprite guides you through the maze-like paths to safety.",
          60: "Jagged peaks scrape the sky as you navigate treacherous mountain passes. The thin air makes every step a challenge, but the view from the summit reveals the vastness of the kingdom you've sworn to protect.",
          80: "The Gold Master's cottage sits atop the highest peak. Ancient and wise, he tests your courage and heart before revealing the truth: 'The Dragon is in the Dark Castle across the Great Plains. You alone can conquer it.'",
        },
      },
      4: {
        theme: "To The Castle",
        icons: {
          20: "river",
          40: "fire",
          60: "dragon",
          80: "castle",
        },
        chapters: {
          20: "The Great River",
          40: "Trial of Fire",
          60: "The Dragon's Lair",
          80: "Castle Approach",
        },
        titles: {
          20: "Raging Waters",
          40: "Flames of Courage",
          60: "Dragon's Breath",
          80: "Royal Rescue",
        },
        messages: {
          20: "The Great River roars before you, swollen with spring rains. With ingenuity, you craft a sturdy raft and navigate the churning waters, narrowly avoiding the jaws of river serpents that lurk beneath the surface.",
          40: "A wall of flame blocks your path. An ancient guardian emerges: 'Only the pure of heart may pass.' You step forward without hesitation, and miraculously, the flames part around you, acknowledging your noble intentions.",
          60: "The dragon's lair reeks of sulfur and smoke. The massive beast awakens with a thunderous roar, but recognizes the royal emblem on your shield. 'Knight of the realm,' it rumbles, 'prove your math skills and I shall let you pass.'",
          80: "The Dark Castle looms against the stormy sky. Guards patrol the walls, but you find a secret entrance. The gold awaits in the highest tower. 'I knew you would come,' the gold whispers. Your greatest challenge - and reward - lies ahead!",
        },
      },
    };

    // Check for milestone progress and show messages
    useEffect(() => {
      // Don't proceed if user is not loaded yet
      if (!user) return;

      // Initialize unit tracking if needed
      if (!highestMilestoneReachedRef.current[unitNumber]) {
        highestMilestoneReachedRef.current[unitNumber] = -1;
      }

      // Initialize shown milestones tracking if needed
      if (!shownMilestonesRef.current[unitNumber]) {
        shownMilestonesRef.current[unitNumber] = [];
        console.log(`Initializing shown milestones for unit ${unitNumber}`);
      }

      // Find highest milestone that has been reached based on current progress
      let currentHighestMilestone = -1;
      for (const milestone of milestones) {
        if (percentage >= milestone) {
          currentHighestMilestone = milestone;
        } else {
          // Break once we find a milestone we haven't reached
          break;
        }
      }

      // Check if we've reached a new highest milestone
      if (
        currentHighestMilestone > highestMilestoneReachedRef.current[unitNumber]
      ) {
        // We've reached a new milestone!
        const newMilestone = currentHighestMilestone;
        console.log(
          `New milestone reached: ${newMilestone}% for unit ${unitNumber}`
        );

        // Update our record of the highest milestone reached
        highestMilestoneReachedRef.current[unitNumber] = newMilestone;

        // Create milestone key for checking
        const milestoneKey = `${unitNumber}-${newMilestone}`;

        // Convert array-based tracking to key-based tracking for more reliability
        const hasShownMilestone =
          shownMilestonesRef.current[unitNumber] &&
          shownMilestonesRef.current[unitNumber].includes(newMilestone);

        console.log(
          `Checking if milestone ${milestoneKey} has been shown:`,
          hasShownMilestone
        );
        console.log(`Current shown milestones:`, shownMilestonesRef.current);

        // Check if we've already shown this milestone
        if (!hasShownMilestone) {
          console.log(
            `Showing milestone message for ${newMilestone}% in unit ${unitNumber} (first time)`
          );

          // Add this milestone to the list of shown milestones
          if (!shownMilestonesRef.current[unitNumber]) {
            shownMilestonesRef.current[unitNumber] = [];
          }

          shownMilestonesRef.current[unitNumber].push(newMilestone);

          // Save updated list to localStorage
          saveShownMilestones(shownMilestonesRef.current);

          console.log("Updated shown milestones:", shownMilestonesRef.current);

          // Set the message and title
          setMessageTitle(
            unitStories[unitNumber].titles[newMilestone] ||
              unitStories[unitNumber].chapters[newMilestone]
          );
          setCurrentMessage(
            unitStories[unitNumber].messages[newMilestone] || ""
          );

          // Set the milestone pending flag before showing the message
          milestonePendingRef.current = true;

          // Show message with a slight delay to ensure it happens after any potential unit change reset
          setTimeout(() => {
            if (milestonePendingRef.current) {
              console.log(
                `Displaying milestone message for ${newMilestone}% in unit ${unitNumber}`
              );
              setShowMessage(true);
              milestonePendingRef.current = false;
            }
          }, 100);

          // Remove the auto-show avatar customizer logic from here
          // It will now only show after clicking "Continue Journey"
        } else {
          console.log(
            `Milestone ${newMilestone}% for unit ${unitNumber} already shown, skipping message`
          );
        }
      }
    }, [percentage, unitNumber, user, unitStories, milestones, userId]);

    // Reset milestone tracking ONLY when unit ACTUALLY changes
    useEffect(() => {
      // Only reset if unit actually changes
      if (unitNumber !== prevUnitNumberRef.current) {
        console.log(
          `Unit ACTUALLY changed from ${prevUnitNumberRef.current} to ${unitNumber}, resetting UI state`
        );
        setShowMessage(false);
        setCurrentMessage("");
        setMessageTitle("");
        milestonePendingRef.current = false; // Cancel any pending milestone messages

        // Update the ref to the new unit number
        prevUnitNumberRef.current = unitNumber;
      }
    }, [unitNumber]);

    // Handle avatar customization completion
    const handleAvatarCustomized = async (avatarOptions) => {
      try {
        // Update user data with avatar options - use avatar_hair_type instead of avatar_gender
        await updateUserData({
          avatar_hair_type: avatarOptions.hairType, // This is the key fix - map hairType to avatar_hair_type
          avatar_hair: avatarOptions.hairColor,
          avatar_shirt: avatarOptions.shirtColor,
          avatar_eyes: avatarOptions.eyeColor,
          avatar_skin: avatarOptions.skinColor,
        });

        // Close customizer
        setShowAvatarCustomizer(false);
      } catch (error) {
        console.error("Failed to update avatar:", error);
      }
    };

    // Modify the milestone modal close handler to check for avatar_hair_type
    const handleCloseMessage = () => {
      setShowMessage(false);

      // After closing the milestone message for Unit 1 at 80%, show avatar customizer if needed
      if (unitNumber === 1 && percentage >= 80 && !user?.avatar_hair_type) {
        setTimeout(() => {
          setShowAvatarCustomizer(true);
        }, 300); // Short delay after closing the message
      }
    };

    // Check for avatar customizer eligibility whenever progress changes
    useEffect(() => {
      // If user is in Unit 1, progress is at least 80%, and they don't have avatar attributes set
      if (
        unitNumber === 1 &&
        percentage >= 80 &&
        user &&
        !user?.avatar_hair_type && // Changed from avatar_gender to avatar_hair_type
        !showMessage && // Don't show during milestone message
        !showAvatarCustomizer
      ) {
        // Not already showing

        // Check if milestone at 80% was shown for unit 1
        const hasShownMilestone =
          shownMilestonesRef.current[1] &&
          shownMilestonesRef.current[1].includes(80);

        // Always show the avatar customizer regardless of whether they've seen the milestone
        // Just use different console logs for debugging purposes
        if (hasShownMilestone) {
          console.log(
            "User has seen milestone but not completed avatar customization, showing now"
          );
        } else {
          console.log(
            "User eligible for avatar customization, showing automatically"
          );
        }

        // Show avatar customizer in both cases
        setTimeout(() => {
          setShowAvatarCustomizer(true);
        }, 500);
      }
    }, [percentage, unitNumber, user, showMessage, showAvatarCustomizer]);

    // Get current chapter name based on progress
    const getCurrentChapter = () => {
      let currentChapter = "";

      for (let i = 0; i < milestones.length; i++) {
        const milestone = milestones[i];
        if (percentage <= milestone) {
          // Before this milestone
          return currentChapter || unitStories[unitNumber].chapters[milestone];
        }
        currentChapter = unitStories[unitNumber].chapters[milestone];
      }

      // After all milestones
      return currentChapter;
    };

    // Add debug logging for progress changes
    useEffect(() => {
      console.log(
        `Progress updated: ${progress} (${percentage}%) for unit ${unitNumber}`
      );
    }, [progress, percentage, unitNumber]);

    // Modify the handleProgressBarClick to call the provided prop function
    const handleProgressBarClick = (e) => {
      e.stopPropagation(); // Stop event from bubbling up
      if (onProgressBarClick) {
        onProgressBarClick(e);
      }
    };

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "10px", // Reduced from 20px to move bar up
        }}
      >
        <style>{`
        @keyframes slide {
          from { background-position: 0% 0; }
          to { background-position: 200% 0; }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translate(-50%, 0); }
          50% { transform: translate(-50%, -5px); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-10px); }
        }

        .progress-container {
          transition: transform 0.2s ease;
          cursor: pointer;
          position: relative;
          overflow: visible !important; /* Ensure overflow is visible */
        }
        
        .progress-container:hover {
          transform: scale(1.00);
        }

        .milestone-icon {
          position: absolute;
          transform: translateX(-50%);
          z-index: 3;
          transition: all 0.3s ease;
        }
        
        .user-avatar {
          position: absolute;
          transform: translateX(-50%);
          z-index: 4;
          animation: bounce 2s infinite;
        }
        
        .chapter-tooltip {
          position: absolute;
          background-color: rgba(255,255,255,0.95);
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.2s ease;
          z-index: 10;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
        }

        .progress-container:hover .chapter-tooltip {
          opacity: 1;
        }

        .progress-bar {
          position: relative;
          width: 100%;
          height: 20px;
          background-color: #f3e1b2;
          border-radius: 10px;
          overflow: visible;
          margin: 10px 0;  /* Reduced margin to move up */
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(to right, #9cdaed,rgb(49, 193, 237), #9cdaed);
          background-size: 200% 100%;
          animation: slide 3s linear infinite;
          transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
          will-change: background-position, width;
          border-radius: 10px;
        }

        .milestone-icon {
          position: absolute;
          z-index: 3;
          transition: all 0.3s ease;
        }
        
        .user-avatar {
          position: absolute;
          z-index: 10; /* Higher z-index to stay on top */
          animation: bounce 2s infinite;
        }

        .milestone-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1000;
          display: flex;
          justify-content: center;
          align-items: center;
          animation: fadeIn 0.3s ease-out;
        }
        
        .milestone-modal {
          background-color: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.2);
          max-width: 90%;
          width: 350px;
          text-align: center;
          position: relative;
          font-family: 'Inter', sans-serif;
        }
        
        .milestone-modal-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 15px;
          color: #333;
        }
        
        .milestone-modal-message {
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 20px;
          color: #555;
        }
        
        .milestone-modal-button {
          background-color: #9cdaed;
          color: #333;
          border: none;
          padding: 8px 20px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.2s;
        }
        
        .milestone-modal-button:hover {
          background-color: #7bc8e0;
          transform: scale(1.05);
        }
      `}</style>

        <div
          ref={progressBarRef}
          className="progress-container"
          style={{
            position: "relative",
            width: "100%",
            padding: "10px 0 30px 0", // Reduced top padding to move up
            transition: "transform 0.2s ease",
            marginTop: "-20px", // Reset any negative margins
          }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          onClick={handleProgressBarClick} // This will now use our enhanced function with stopPropagation
        >
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${percentage}%` }}
            />

            {/* 80% milestone marker */}
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "80%",
                width: "2px",
                backgroundColor: "#ff9e3d",
                zIndex: 2,
                opacity: 0.8,
              }}
            />
          </div>

          {/* Render milestone icons - PROPERLY POSITIONED */}
          {milestones.map((milestone) => {
            const isPassed = percentage >= milestone;

            return (
              <div
                key={milestone}
                className="milestone-icon"
                style={{
                  left: `${milestone}%`,
                  // Position adjustments:
                  // 1. Uncompleted milestones ON the bar (centered)
                  // 2. Completed milestones BELOW the bar (further down)
                  top: isPassed ? "45px" : "30px", // Move completed ones further down, uncompleted ones centered
                  opacity: isPassed ? 1 : 0.6,
                  transform: `translateX(-50%) ${
                    isPassed ? "" : "translateY(-50%)"
                  }`, // Center horizontally for all, vertically only for uncompleted
                }}
              >
                <StoryMilestone
                  icon={unitStories[unitNumber].icons[milestone]}
                  achieved={isPassed}
                  size={30} // Slightly larger for better visibility
                  showTooltip={hovering}
                  tooltipText={unitStories[unitNumber].chapters[milestone]}
                />
              </div>
            );
          })}

          {/* User avatar indicator - Position ON the progress bar */}
          <div
            className="user-avatar"
            style={{
              left: `${percentage}%`,
              top: "18px", // Now centered exactly on the bar
              transform: "translate(-50%, -50%)", // Center horizontally and vertically
              zIndex: 10, // Ensure it appears above other elements
            }}
          >
            <UserAvatar user={user} size={28} />
          </div>
        </div>

        {/* Removed the unit text section as requested */}

        {showAvatarCustomizer && (
          <AvatarCustomizer
            onComplete={handleAvatarCustomized}
            onCancel={() => setShowAvatarCustomizer(false)}
          />
        )}

        {/* Story Milestone Modal - centered in the viewport with no overlay */}
        {showMessage && currentMessage && (
          <div
            style={{
              position: "fixed",
              top: 100,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              pointerEvents: "none", // This allows clicks to pass through the container
            }}
          >
            <div
              className="milestone-modal"
              style={{
                pointerEvents: "auto", // Re-enable pointer events just for the modal
                animation: "fadeIn 0.3s ease-out",
                boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              }}
              onClick={(e) => e.stopPropagation()} // Add stopPropagation to prevent clicks from bubbling
            >
              <div className="milestone-modal-title">{messageTitle}</div>
              <div className="milestone-modal-message">{currentMessage}</div>
              <button
                className="milestone-modal-button"
                onClick={(e) => {
                  e.stopPropagation(); // Stop propagation here too
                  handleCloseMessage();
                }}
              >
                Continue Journey
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default ProgressBar;
